import { Link } from "react-router-dom";
import { ArrowLeft, CreditCard } from "lucide-react";

export default function TradingTermsPage() {
  return (
    <div className="min-h-screen bg-[#09090b]">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        
        {/* Back Link */}
        <Link to="/" className="inline-flex items-center gap-2 text-zinc-400 hover:text-white mb-8 transition-colors">
          <ArrowLeft className="h-4 w-4" />
          Tilbage til forsiden
        </Link>

        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <div className="w-12 h-12 rounded-xl bg-[#DFFF00]/10 flex items-center justify-center">
            <CreditCard className="h-6 w-6 text-[#DFFF00]" />
          </div>
          <div>
            <h1 style={{ fontFamily: 'Oswald, sans-serif' }} className="text-3xl font-bold text-white uppercase tracking-tight">
              Handelsbetingelser
            </h1>
            <p className="text-sm text-zinc-500">Sidst opdateret: 1. marts 2025</p>
          </div>
        </div>

        {/* Content */}
        <div className="prose prose-invert prose-zinc max-w-none">
          <div className="bg-[#18181b] border border-white/5 rounded-xl p-6 sm:p-8 space-y-8">
            
            <section>
              <h2 className="text-xl font-bold text-white mb-4">1. Generelt</h2>
              <p className="text-zinc-400 leading-relaxed">
                Disse handelsbetingelser gælder for alle køb af abonnementer på FitrackR, der drives af 
                <span className="text-[#DFFF00]"> [FIRMANAVN]</span>, CVR-nr. <span className="text-[#DFFF00]">[CVR-NUMMER]</span>.
              </p>
              <p className="text-zinc-400 leading-relaxed mt-4">
                Ved at købe et abonnement accepterer du disse handelsbetingelser samt vores generelle vilkår og privatlivspolitik.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-white mb-4">2. Produktbeskrivelse</h2>
              <p className="text-zinc-400 leading-relaxed">
                FitrackR er en digital fitness-platform, der giver adgang til:
              </p>
              <ul className="list-disc list-inside text-zinc-400 mt-4 space-y-2">
                <li>AI-genererede træningsprogrammer tilpasset dine mål</li>
                <li>Personlige madplaner med indkøbslister</li>
                <li>Kropsdata-tracking (vægt, mål, fremskridt)</li>
                <li>Søvn- og vandindtag-tracking</li>
                <li>Gamification med achievements og leaderboard</li>
                <li>Øvelsesbibliotek med videoguides</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-bold text-white mb-4">3. Priser</h2>
              <div className="p-4 bg-gradient-to-r from-[#DFFF00]/10 to-transparent rounded-lg border border-[#DFFF00]/20">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white font-bold text-lg">FitrackR Pro</p>
                    <p className="text-zinc-400 text-sm">Månedligt abonnement</p>
                  </div>
                  <div className="text-right">
                    <p className="text-[#DFFF00] font-bold text-2xl" style={{ fontFamily: 'JetBrains Mono' }}>49 DKK</p>
                    <p className="text-zinc-500 text-sm">per måned inkl. moms</p>
                  </div>
                </div>
              </div>
              <p className="text-zinc-400 leading-relaxed mt-4">
                Alle priser er angivet i danske kroner (DKK) og inkluderer 25% moms.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-white mb-4">4. Gratis Prøveperiode</h2>
              <p className="text-zinc-400 leading-relaxed">
                Nye brugere får en <strong className="text-white">3-dages gratis prøveperiode</strong> ved tilmelding.
              </p>
              <ul className="list-disc list-inside text-zinc-400 mt-4 space-y-2">
                <li>Der kræves et gyldigt betalingskort ved tilmelding</li>
                <li>Du får fuld adgang til alle funktioner i prøveperioden</li>
                <li>Du kan opsige når som helst inden prøveperioden udløber uden beregning</li>
                <li>Hvis du ikke opsiger, trækkes første betaling automatisk efter 3 dage</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-bold text-white mb-4">5. Betaling</h2>
              <p className="text-zinc-400 leading-relaxed">
                Vi accepterer følgende betalingsmetoder via vores betalingspartner Stripe:
              </p>
              <ul className="list-disc list-inside text-zinc-400 mt-4 space-y-2">
                <li>Visa og Mastercard (kredit- og debetkort)</li>
                <li>Apple Pay og Google Pay</li>
              </ul>
              <p className="text-zinc-400 leading-relaxed mt-4">
                Betalingen trækkes automatisk hver måned på samme dato som din oprindelige tilmelding.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-white mb-4">6. Fortrydelsesret</h2>
              <p className="text-zinc-400 leading-relaxed">
                I henhold til forbrugeraftaleloven har du 14 dages fortrydelsesret fra det tidspunkt, 
                hvor du indgår aftalen om et abonnement.
              </p>
              <div className="mt-4 p-4 bg-orange-500/10 border border-orange-500/20 rounded-lg">
                <p className="text-orange-400 font-semibold mb-2">Vigtigt om fortrydelsesretten:</p>
                <p className="text-zinc-400 text-sm">
                  Hvis du aktivt bruger tjenesten i fortrydelsesperioden (f.eks. genererer træningsprogrammer eller madplaner), 
                  giver du samtykke til, at ydelsen påbegyndes straks. I så fald kan din fortrydelsesret bortfalde helt 
                  eller delvist proportionalt med den leverede ydelse.
                </p>
              </div>
              <p className="text-zinc-400 leading-relaxed mt-4">
                For at udøve din fortrydelsesret skal du kontakte os skriftligt på <span className="text-[#DFFF00]">[EMAIL]</span> inden fristens udløb.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-white mb-4">7. Opsigelse</h2>
              <p className="text-zinc-400 leading-relaxed">
                Du kan opsige dit abonnement når som helst:
              </p>
              <ul className="list-disc list-inside text-zinc-400 mt-4 space-y-2">
                <li>Opsigelse kan ske via din profilside eller ved at kontakte os</li>
                <li>Opsigelsen træder i kraft ved udgangen af den aktuelle betalingsperiode</li>
                <li>Du beholder adgang til tjenesten indtil perioden udløber</li>
                <li>Der ydes ingen refusion for ubrugt tid</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-bold text-white mb-4">8. Refusion</h2>
              <p className="text-zinc-400 leading-relaxed">
                Som udgangspunkt ydes der ikke refusion for allerede betalte abonnementsperioder.
              </p>
              <p className="text-zinc-400 leading-relaxed mt-4">
                Undtagelser kan gøres i tilfælde af:
              </p>
              <ul className="list-disc list-inside text-zinc-400 mt-4 space-y-2">
                <li>Tekniske fejl, der forhindrer brug af tjenesten i en længere periode</li>
                <li>Fejlagtig dobbeltopkrævning</li>
                <li>Særlige omstændigheder efter konkret vurdering</li>
              </ul>
              <p className="text-zinc-400 leading-relaxed mt-4">
                Kontakt os på <span className="text-[#DFFF00]">[EMAIL]</span> for at anmode om refusion.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-white mb-4">9. Levering</h2>
              <p className="text-zinc-400 leading-relaxed">
                FitrackR er en digital tjeneste. Adgang til platformen gives øjeblikkeligt efter gennemført betaling 
                eller aktivering af prøveperiode. Der er ingen fysisk levering.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-white mb-4">10. Ansvarsfraskrivelse</h2>
              <p className="text-zinc-400 leading-relaxed">
                FitrackR leverer trænings- og madplaner genereret af kunstig intelligens til vejledende brug.
              </p>
              <div className="mt-4 p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
                <p className="text-red-400 font-semibold mb-2">Vigtigt:</p>
                <ul className="text-zinc-400 text-sm space-y-2">
                  <li>Vores indhold erstatter ikke professionel rådgivning fra læger, diætister eller personlige trænere</li>
                  <li>Konsultér altid en læge før du påbegynder et nyt trænings- eller kostprogram</li>
                  <li>Vi påtager os intet ansvar for skader opstået ved brug af vores tjeneste</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-bold text-white mb-4">11. Klager</h2>
              <p className="text-zinc-400 leading-relaxed">
                Har du en klage, bedes du kontakte os på <span className="text-[#DFFF00]">[EMAIL]</span>. Vi bestræber os på at besvare alle henvendelser inden for 5 arbejdsdage.
              </p>
              <p className="text-zinc-400 leading-relaxed mt-4">
                Hvis vi ikke kan løse din klage, kan du indgive en klage til:
              </p>
              <div className="mt-4 p-4 bg-zinc-900/50 rounded-lg">
                <p className="text-white font-semibold">Center for Klageløsning</p>
                <p className="text-zinc-400">Nævnenes Hus</p>
                <p className="text-zinc-400">Toldboden 2</p>
                <p className="text-zinc-400">8800 Viborg</p>
                <p className="text-zinc-400 mt-2">Web: <a href="https://naevneneshus.dk" target="_blank" rel="noopener noreferrer" className="text-[#DFFF00] hover:underline">naevneneshus.dk</a></p>
              </div>
              <p className="text-zinc-400 leading-relaxed mt-4">
                Du kan også benytte EU-Kommissionens online klageportal: <a href="https://ec.europa.eu/odr" target="_blank" rel="noopener noreferrer" className="text-[#DFFF00] hover:underline">ec.europa.eu/odr</a>
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-white mb-4">12. Kontaktoplysninger</h2>
              <div className="p-4 bg-zinc-900/50 rounded-lg">
                <p className="text-[#DFFF00] font-semibold">[FIRMANAVN]</p>
                <p className="text-zinc-400">[ADRESSE]</p>
                <p className="text-zinc-400">[POSTNUMMER] [BY]</p>
                <p className="text-zinc-400 mt-2">CVR: [CVR-NUMMER]</p>
                <p className="text-zinc-400">Email: [EMAIL]</p>
              </div>
            </section>

          </div>
        </div>

        {/* Footer Links */}
        <div className="mt-8 pt-8 border-t border-white/5 flex flex-wrap gap-4 text-sm text-zinc-500">
          <Link to="/terms" className="hover:text-[#DFFF00] transition-colors">Vilkår & Betingelser</Link>
          <span>•</span>
          <Link to="/privacy" className="hover:text-[#DFFF00] transition-colors">Privatlivspolitik</Link>
          <span>•</span>
          <Link to="/cookies" className="hover:text-[#DFFF00] transition-colors">Cookie-politik</Link>
        </div>
      </div>
    </div>
  );
}
